/* ============================================================
 * frontend-v3/src/lib/folderTree.ts
 *
 * NMW-931 (parent NMW-913) — the small, pure shape helpers shared by
 * `FolderBreadcrumbs` and `MoveToFolderDialog` so the Library and the
 * Channels screens cannot drift apart on how a folder tree is read.
 *
 * The backend (NMW-928) caps the tree at depth 2 and `convex/folders.ts`
 * is its sole writer. These helpers only READ a flat folder list — the
 * shape returned by `api.folders.listFolders` — and never fetch.
 *
 * Deliberately dependency-free and framework-free: unit-testable without
 * jsdom, and importable from any screen.
 * ============================================================ */

/** Which plane a folder organizes. A folder is never cross-domain. */
export type FolderDomain = 'asset' | 'channel';

/**
 * The subset of a `folders` row the UI needs. Structurally compatible with
 * the Convex document (extra fields such as `_creationTime` / `createdAtMs`
 * are simply ignored), so a screen can pass the query result straight in.
 */
export interface FolderNode {
  _id: string;
  name: string;
  /** Absent / null ⇒ a depth-1 (root) folder. */
  parentId?: string | null;
  depth?: number;
  domain?: FolderDomain;
}

/** One depth-1 folder plus its depth-2 children, both name-sorted. */
export interface FolderTreeNode {
  folder: FolderNode;
  children: FolderNode[];
}

function byName(a: FolderNode, b: FolderNode): number {
  return a.name.localeCompare(b.name);
}

/**
 * Group a flat list into the two-level tree the picker renders.
 *
 * A row whose `parentId` names a folder that is NOT in the list is an
 * orphan — it is surfaced as a root rather than silently dropped, because
 * a folder the operator cannot see is a folder they cannot move out of.
 */
export function buildFolderTree(
  folders: readonly FolderNode[],
): FolderTreeNode[] {
  const byId = new Map(folders.map((f) => [f._id, f]));
  const roots: FolderNode[] = [];
  const children = new Map<string, FolderNode[]>();

  for (const f of folders) {
    const parentId = f.parentId ?? undefined;
    if (parentId === undefined || !byId.has(parentId)) {
      roots.push(f);
      continue;
    }
    const bucket = children.get(parentId);
    if (bucket) bucket.push(f);
    else children.set(parentId, [f]);
  }

  return roots.sort(byName).map((folder) => ({
    folder,
    children: (children.get(folder._id) ?? []).sort(byName),
  }));
}

/**
 * The root-most-first chain of folders leading to `folderId`.
 *
 * `null` / `undefined` ⇒ `[]` (the operator is at the domain root).
 * An id that does not resolve ⇒ `[]` as well; the CALLER decides how to be
 * honest about it (`FolderBreadcrumbs` renders an explicit unknown-folder
 * crumb rather than pretending the operator is at the root).
 *
 * Cycle-safe: a `parentId` loop — impossible through the NMW-928 mutations
 * but cheap to defend against — terminates instead of hanging the render.
 */
export function folderTrail(
  folders: readonly FolderNode[],
  folderId: string | null | undefined,
): FolderNode[] {
  if (!folderId) return [];
  const byId = new Map(folders.map((f) => [f._id, f]));
  const trail: FolderNode[] = [];
  const seen = new Set<string>();
  let cursor: string | null | undefined = folderId;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const node = byId.get(cursor);
    if (!node) return [];
    trail.unshift(node);
    cursor = node.parentId ?? undefined;
  }
  return trail;
}
